// InvitePage — magic-link redemption.
//
// Reached via `/invite/<token>` (no router lib — App.js peeks at the URL on
// load). We:
//   1. GET /api/invite/<token>/ to confirm the token is valid + pre-fill email.
//   2. Render a tiny signup form (just password) — email is locked.
//   3. POST /api/auth/signup-with-invite/ on submit; on success store the
//      returned Knox token and call onLogin so the app drops into Home.
//
// If the token check fails, show a clear "invite isn't valid" screen with a
// link back to the waitlist form so they can re-request.

import { useEffect, useState } from 'react';
import api from './api';
import Header from './components/Header';

export default function InvitePage({ token, onLogin, onBackToWelcome }) {
  const [status, setStatus] = useState('checking'); // checking | valid | invalid
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    api.get(`/invite/${encodeURIComponent(token)}/`)
      .then((res) => {
        const data = res.data?.data;
        if (data?.email) {
          setEmail(data.email);
          setName(data.name || '');
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords don\'t match.'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/auth/signup-with-invite/', { token, password });
      const tok = res.data?.data?.token;
      if (!tok) throw new Error('No token returned.');
      localStorage.setItem('token', tok);
      // Clear the /invite/... URL out of the address bar so a refresh doesn't
      // re-trigger this page.
      window.history.replaceState({}, '', '/');
      onLogin();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not complete signup.');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="auth-shell">
        <Header title="Claim your spot" />
        <div className="auth-form-body" style={{ alignItems: 'center', justifyContent: 'center' }}>
          <span className="spinner" style={{ color: 'var(--t-purple)' }} />
          <div style={{ marginTop: 12, color: 'var(--t-muted)' }}>Checking your invite…</div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="auth-shell">
        <Header title="Invite" onBack={onBackToWelcome} />
        <div className="auth-form-body">
          <h2>This invite isn't valid.</h2>
          <p className="auth-sub">
            The link may have expired, already been used, or been mistyped.
            Re-join the waitlist and we'll send a fresh one.
          </p>
          <div style={{ flex: 1 }} />
          <button className="btn-primary" onClick={onBackToWelcome}>Back to waitlist</button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <Header title="Claim your spot" />
      <div className="auth-form-body">
        <div>
          <h2>Welcome{name ? `, ${name.split(' ')[0]}` : ''} 👋</h2>
          <div className="auth-sub">You're off the waitlist. Pick a password to finish setting up your account.</div>
        </div>

        {error && <div className="alert error">{error}</div>}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label className="form-label">Email</label>
            <input type="email" value={email} disabled readOnly autoComplete="email" />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input type="password" placeholder="At least 8 characters" value={password}
                   onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
          </div>
          <div>
            <label className="form-label">Confirm password</label>
            <input type="password" placeholder="Re-enter password" value={confirmPassword}
                   onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn-primary" type="submit" disabled={submitting}>
            {submitting
              ? <><span className="spinner" /> Creating account…</>
              : 'Create account & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
